<template>
  <div v-if="requestSent" class="text-center">
    <p data-test="forgot-password-sent">
      If an account exists for this email, we've sent a password reset link. It expires in {{ PASSWORD_RESET_LINK_TTL_MINUTES }} minutes.
    </p>
    <a href="#" @click.prevent="$emit('click:back')"><strong>Login to my account</strong></a>
  </div>
  <v-form
    v-else
    ref="form"
    lazy-validation
    @submit.prevent="handleSubmit"
  >
    <p>Enter the email address associated with your account, and we'll send you a link to reset your password.</p>
    <v-text-field
      v-model="email"
      label="E-mail"
      :rules="EMAIL_RULES"
      outlined
      placeholder="Email Address"
      autocapitalize="none"
      type="email"
    />
    <div class="text-center">
      <div
        v-if="captchaRequired"
        :id="CAPTCHA_CONTAINER_ID"
        ref="captcha"
        class="captcha-container d-flex justify-center mb-4"
      />
      <v-btn
        class="primary mb-5"
        :loading="requestLoading"
        type="submit"
      >
        Send Reset Link
      </v-btn>
      <v-spacer />
      <a href="#" @click.prevent="$emit('click:back')"><strong>Login to my account</strong></a>
    </div>
  </v-form>
</template>

<script lang="ts">
import Vue from 'vue';

import { authService } from '@/bundles/Auth/factory/authServiceFactory';
import { PASSWORD_RESET_LINK_TTL_MINUTES } from '@/bundles/Auth/consts';
import { isPentestEmail } from '@/bundles/Auth/helpers';
import { renderCaptcha, resetCaptcha, validateCaptcha } from '@/plugins/captcha';
import { emailPattern } from '@/utils/validation';
import { VForm } from '@/types/common';

const CAPTCHA_CONTAINER_ID = 'captcha-forgot-password-container';
const EMAIL_RULES: ReadonlyArray<(value: string) => boolean | string> = Object.freeze([
  (value: string) => !!value || 'E-mail is required',
  (value: string) => emailPattern.test(value) || 'E-mail must be valid',
]);

interface IData {
  CAPTCHA_CONTAINER_ID: string;
  PASSWORD_RESET_LINK_TTL_MINUTES: number;
  EMAIL_RULES: typeof EMAIL_RULES;
  email: string;
  requestLoading: boolean;
  requestSent: boolean;
}

export default Vue.extend({
  name: 'ForgotPasswordRequestForm',

  data: (): IData => ({
    CAPTCHA_CONTAINER_ID,
    PASSWORD_RESET_LINK_TTL_MINUTES,
    EMAIL_RULES,
    email: '',
    requestLoading: false,
    requestSent: false,
  }),

  computed: {
    normalizedEmail (): string {
      return this.email.toLowerCase().trim();
    },
    captchaRequired (): boolean {
      return !isPentestEmail(this.normalizedEmail);
    },
  },

  watch: {
    captchaRequired (value: boolean) {
      if (value) {
        this.renderCaptcha();
      }
    },
  },

  mounted () {
    this.renderCaptcha();
  },

  methods: {
    renderCaptcha () {
      this.$nextTick(() => renderCaptcha(CAPTCHA_CONTAINER_ID));
    },
    async handleSubmit () {
      if (!(this.$refs.form as VForm).validate()) {
        return;
      }

      const captchaResponse = this.captchaRequired ? validateCaptcha() : true;
      if (!captchaResponse) {
        (this.$refs.captcha as HTMLElement | undefined)?.classList.add('captcha-container--error');
        return;
      }

      this.requestLoading = true;
      try {
        await authService.forgotPasswordRequest({
          email: this.normalizedEmail,
          ...(typeof captchaResponse === 'string' && { response: captchaResponse }),
        });
        this.requestSent = true;
      } catch (error: any) {
        this.addNotification({ ...error, status: 0 });
        resetCaptcha();
      } finally {
        this.requestLoading = false;
      }
    },
  },
});
</script>
